import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useAuth } from "../AuthContext";
import LanguageToggle from "../components/LanguageToggle";
import { formatApiError } from "../api";
import { Eye, EyeSlash } from "@phosphor-icons/react";

const GOLD = "#C9A84C";

// AXIS storefront–themed distributor entry point. White + gold, minimal —
// designed to feel like a seamless extension of the storefront, not the
// internal ReUP admin. Reuses the existing secure cookie/JWT auth flow.
export default function DistributorLoginPage() {
  const { login, user } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const destFor = (u) => (u?.role === "distributor" ? "/distributor/dashboard" : "/");

  useEffect(() => {
    if (user && user !== false) nav(destFor(user), { replace: true });
  }, [user, nav]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(email.trim().toLowerCase(), password);
      toast.success(t("auth.welcome_back"));
      nav(destFor(data), { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-160px)] flex items-center justify-center py-10" data-testid="distributor-login-page">
      <div className="w-full max-w-sm">
        <div className="flex justify-end mb-6">
          <LanguageToggle />
        </div>

        <div className="text-center mb-10">
          <p className="text-[26px] font-light tracking-[0.5em] text-zinc-950 select-none" aria-label="AXIS">
            AXIS
          </p>
          <div className="w-10 h-px mx-auto mt-3 mb-4" style={{ backgroundColor: GOLD }} />
          <p className="text-[11px] font-semibold uppercase tracking-[0.3em]" style={{ color: GOLD }}>
            {t("dist_portal.title")}
          </p>
          <p className="text-sm text-zinc-500 mt-3">{t("dist_portal.tagline")}</p>
        </div>

        <div className="bg-white rounded-2xl p-6 sm:p-8" style={{ border: `1px solid ${GOLD}` }} data-testid="distributor-login-card">
          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-700 mb-2">
                {t("common.email")}
              </label>
              <input
                data-testid="distributor-login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="w-full h-11 px-3.5 rounded-lg border border-zinc-300 bg-white text-sm outline-none transition-colors focus:border-[#C9A84C] focus:ring-2 focus:ring-[#C9A84C]/25"
              />
            </div>
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-700 mb-2">
                {t("common.password")}
              </label>
              <div className="relative">
                <input
                  data-testid="distributor-login-password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full h-11 px-3.5 pr-11 rounded-lg border border-zinc-300 bg-white text-sm outline-none transition-colors focus:border-[#C9A84C] focus:ring-2 focus:ring-[#C9A84C]/25"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-[#C9A84C] transition-colors p-1"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  data-testid="distributor-toggle-password"
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            <div className="text-right">
              <Link
                to="/forgot-password"
                className="text-xs font-semibold uppercase tracking-[0.14em] text-[#8a6a1c] hover:text-[#C9A84C] transition-colors"
                data-testid="distributor-forgot-password-link"
              >
                {t("auth.forgot_password")}
              </Link>
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="distributor-login-submit"
              className="w-full h-11 rounded-lg text-white text-sm font-semibold uppercase tracking-[0.18em] transition-colors disabled:opacity-60 bg-[#C9A84C] hover:bg-[#B8923F]"
            >
              {loading ? t("dist_portal.signing_in") : t("dist_portal.sign_in")}
            </button>
          </form>
        </div>

        <p className="text-xs text-zinc-500 text-center mt-6 leading-relaxed" data-testid="distributor-login-hint">
          {t("dist_portal.no_access_hint")}
        </p>
      </div>
    </div>
  );
}
